import { zodResolver } from '@hookform/resolvers/zod'
import * as Form from '@radix-ui/react-form'
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons'
import { useNavigate } from '@tanstack/react-router'
import { message, Tooltip } from 'antd'
import { SalesforceSelect } from 'components/salesforce/SalesforceSelect'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useFilteredSalesforceAccountsForSitesUnderParent } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/salesforceData'
import { StyledSelectDiv } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/sharedStyles'
import {
  formValidationStyles,
  StyledFormInput,
  StyledFormSubmitButton,
} from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import {
  generateEntrySlug,
  isValidEntrySlug,
  useRolodexEntries,
} from 'features/rolodex'
import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import styled from 'styled-components'
import {
  GeckoOrganization,
  GeckoSite,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import { defaultValues, EditSiteSchema } from './editSiteSchema'

const StyledSiteContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${formValidationStyles}
`

const StyledHeaderDiv = styled.div`
  padding-bottom: 15px;
`

const StyledFormRoot = styled(Form.Root)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const FormLabelWithTooltip = styled(Form.Label)`
  display: flex;
  gap: 4px;
  align-items: center;
`

export const EditGeckoSiteProps: RightSideDrawerProps = {
  title: 'Edit Gecko Site',
}

const newSiteEntry = {
  id: 'test',
  type: 'site',
  data: {},
  tags: {},
} as RolodexEntryPutJson

export const EditGeckoSite = () => {
  const { drawerEntry, closeDrawer } = useRightDrawer()
  const { rolodexPut } = useRolodexEdit()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()
  const [orgSlug, setOrgSlug] = useState<string>('')
  const [siteSlug, setSiteSlug] = useState<string>('')
  const form = useForm<EditSiteSchema>({
    mode: 'onBlur',
    resolver: zodResolver(EditSiteSchema),
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = form
  const watchAllFields = watch()

  //creating the hash for the site slug
  const siteHash = useMemo(() => uuid4().slice(0, 6), [])

  // Only update the slug if we passed in a GeckoOrganization to state (since we're adding a new site)
  const updateSlug = (e: BaseSyntheticEvent) => {
    if (drawerEntry instanceof GeckoOrganization) {
      const entrySlug = generateEntrySlug(e.target.value, siteHash)
      if (isValidEntrySlug(entrySlug)) setSiteSlug(entrySlug)
    }
  }
  // if there is a site entry (ie editing) then populate the slug field
  useEffect(() => {
    if (drawerEntry) {
      if (drawerEntry instanceof GeckoSite) {
        setSiteSlug(drawerEntry.raw.id)
        setOrgSlug(drawerEntry.tags.organization)
        reset({
          siteName: drawerEntry.data.name ?? 'Unknown Name',
          siteSlackChannel: drawerEntry.data.slack_channel ?? '',
          sitePrimaryAddress: drawerEntry.data.address?.address_line_1 ?? '',
          siteSecondaryAddress: drawerEntry.data.address?.address_line_2 ?? '',
          siteRegion: drawerEntry.data.address?.region ?? '',
          siteCity: drawerEntry.data.address?.city ?? '',
          siteCountry: drawerEntry.data.address?.country ?? '',
          sitePostalCode: drawerEntry.data.address?.postal_code ?? '',
          salesforceAccount:
            drawerEntry.tags[RolodexEntryType.SALESFORCE_ACCOUNT],
        })
      } else {
        setOrgSlug(drawerEntry.id)
      }
    }
  }, [drawerEntry, reset, setValue])

  const containerTitle = drawerEntry instanceof GeckoSite ? 'Editing' : 'Adding'
  const submitButtonText = drawerEntry instanceof GeckoSite ? 'Save' : 'Create'

  const { organizations: parentOrganization } = useRolodexEntries({
    type: RolodexEntryType.ORGANIZATION,
    filters: [
      {
        field: 'id',
        op: '=',
        value: orgSlug,
      },
    ],
  })
  const parentOrgSalesforceAccountId =
    parentOrganization?.[0]?.tags[RolodexEntryType.SALESFORCE_ACCOUNT]
  const { salesforceAccountOptions, salesforceAccountIdToNameMap } =
    useFilteredSalesforceAccountsForSitesUnderParent(
      parentOrgSalesforceAccountId,
      drawerEntry?.raw.tags[RolodexEntryType.SALESFORCE_ACCOUNT] ?? undefined
    )

  const onSubmit: SubmitHandler<EditSiteSchema> = (data) => {
    const siteRawJSON =
      drawerEntry instanceof GeckoSite ? drawerEntry.raw : newSiteEntry
    const addressObject = {
      address_line_1: data.sitePrimaryAddress,
      address_line_2: data.siteSecondaryAddress,
      city: data.siteCity,
      region: data.siteRegion,
      country: data.siteCountry,
      postal_code: data.sitePostalCode,
    }

    siteRawJSON.id = siteSlug
    siteRawJSON.data.slack_channel = data.siteSlackChannel
    siteRawJSON.data.name = data.siteName
    siteRawJSON.data.address = addressObject
    siteRawJSON.tags.organization = orgSlug
    if (data.salesforceAccount) {
      siteRawJSON.tags[RolodexEntryType.SALESFORCE_ACCOUNT] =
        data.salesforceAccount
    }
    const action = drawerEntry ? 'modified this' : 'created new'
    rolodexPut([siteRawJSON])
      .then(() => {
        return messageApi.success(
          `Successfully ${action} site! Redirecting...`,
          1
        )
      })
      .then(() => {
        closeDrawer()
        setTimeout(
          () =>
            navigate({
              to: '/assets/{-$siteID}',
              params: {
                siteID: siteRawJSON.id,
              },
              replace: true,
            }),
          1800
        )
      })
      .catch(() => {
        messageApi.error(
          `Failed to ${action} site! Please check permissions and try again.`
        )
      })
  }

  return (
    <FormProvider {...form}>
      <StyledSiteContainer>
        {contextHolder}
        <div>
          <StyledHeaderDiv>
            <h3>
              {containerTitle}: {watchAllFields.siteName}
            </h3>
            <h4>Slug: {siteSlug}</h4>
          </StyledHeaderDiv>
        </div>
        <StyledFormRoot onSubmit={handleSubmit(onSubmit)}>
          {/* Register your input into the hook by using a Controller component */}
          <Controller
            control={control}
            name='siteName'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Site Name</label>
                <StyledFormInput
                  data-testid='siteName'
                  onChange={(e) => {
                    updateSlug(e)
                    onChange(e)
                  }}
                  onBlur={onBlur}
                  placeholder='Site Name'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.siteName}
                />
                {!isValidEntrySlug(siteSlug) && (
                  <span role='alert'>
                    {errors.siteName && errors.siteName.message}
                  </span>
                )}
              </>
            )}
          />
          <Controller
            name='salesforceAccount'
            control={control}
            render={({ field: { name, onBlur, onChange, value, ref } }) => (
              <StyledSelectDiv>
                <label htmlFor={name}>Salesforce Account</label>
                <SalesforceSelect
                  id={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  data={salesforceAccountOptions}
                  groupByParent={true}
                  salesforceAccountIdToNameMap={salesforceAccountIdToNameMap}
                />
                {errors.salesforceAccount && (
                  <span role='alert'>{errors.salesforceAccount.message}</span>
                )}
              </StyledSelectDiv>
            )}
          />

          <Controller
            control={control}
            name='siteSlackChannel'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    justifyItems: 'center',
                  }}>
                  <label>Slack Channel</label>
                  <Tooltip
                    title={
                      'Note that you must manually create a channel in slack with this name'
                    }
                    placement='top'>
                    <QuestionMarkCircledIcon />
                  </Tooltip>
                </div>

                <StyledFormInput
                  data-testid='siteSlackChannel'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Slack Channel'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.siteSlackChannel}
                />
                <span role='alert'>
                  {errors.siteSlackChannel && errors.siteSlackChannel.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='sitePrimaryAddress'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Primary Address</label>
                <StyledFormInput
                  data-testid='sitePrimaryAddress'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Primary Address'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.sitePrimaryAddress}
                />
                <span role='alert'>
                  {errors.sitePrimaryAddress &&
                    errors.sitePrimaryAddress.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='siteSecondaryAddress'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Secondary Address (optional)</label>
                <StyledFormInput
                  data-testid='siteSecondaryAddress'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Secondary Address'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.siteSecondaryAddress}
                />
                <span role='alert'>
                  {errors.siteSecondaryAddress &&
                    errors.siteSecondaryAddress.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='siteCity'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>City</label>
                <StyledFormInput
                  data-testid='siteCity'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='City'
                  value={value}
                  aria-invalid={!!errors.siteCity}
                  ref={ref}
                />
                <span role='alert'>
                  {errors.siteCity && errors.siteCity.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='siteRegion'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Region/State</label>
                <StyledFormInput
                  data-testid='siteRegion'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Region'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.siteRegion}
                />
                <span role='alert'>
                  {errors.siteRegion && errors.siteRegion.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='siteCountry'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Country</label>
                <StyledFormInput
                  data-testid='siteCountry'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Country'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.siteCountry}
                />
                <span role='alert'>
                  {errors.siteCountry && errors.siteCountry.message}
                </span>
              </>
            )}
          />
          <Controller
            control={control}
            name='sitePostalCode'
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <>
                <label>Postal Code</label>
                <StyledFormInput
                  data-testid='sitePostalCode'
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='Postal Code'
                  value={value}
                  ref={ref}
                  aria-invalid={!!errors.sitePostalCode}
                />
                <span>
                  {errors.sitePostalCode && errors.sitePostalCode.message}
                </span>
              </>
            )}
          />
          <StyledFormSubmitButton htmlType={'submit'}>
            {submitButtonText}
          </StyledFormSubmitButton>
        </StyledFormRoot>
      </StyledSiteContainer>
    </FormProvider>
  )
}
